/**
 * Terminal View Component
 *
 * Real terminal interface using xterm.js with WebSocket communication
 * to PTY sessions on the backend.
 */

import { useEffect, useRef, useCallback, useState, Fragment } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useTerminalStore, type TerminalTab } from "@/stores/terminalStore";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceStore, type ContainerStatus } from "@/stores/workspaceStore";
import { cn } from "@/lib/utils";
import { Plus, X, Loader2, AlertCircle, RefreshCw, LayoutGrid, Rows2, Moon, Cloud } from "lucide-react";
import { terminalTheme, terminalConfig } from "./terminal-theme";
import { useTerminalFocusRefit } from "@/hooks/useTerminalFocusRefit";
import { ToolLauncherButtons } from "./ToolLauncherButtons";

import "@xterm/xterm/css/xterm.css";

interface TerminalViewProps {
  projectId: number;
  className?: string;
}

export function TerminalView({ projectId, className }: TerminalViewProps) {
  const {
    tabs,
    activeTabId,
    createTab,
    closeTab,
    setActiveTab,
    connect,
    sendInput,
    sendResize,
    updateTabStatus,
  } = useTerminalStore();

  const { getAuthToken } = useAuth();

  const { getActiveWorkspace, createContainer, wakeContainer } = useWorkspaceStore();
  const activeWorkspace = getActiveWorkspace();
  const containerStatus: ContainerStatus = activeWorkspace?.containerStatus || "none";
  const isCloudWorkspace = activeWorkspace?.type === "cloud";

  // Track terminals and fit addons per tab
  const terminalsRef = useRef<Map<string, Terminal>>(new Map());
  const fitAddonsRef = useRef<Map<string, FitAddon>>(new Map());
  const containerRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const containerRefCallbacks = useRef<Map<string, (el: HTMLDivElement | null) => void>>(new Map());
  const pendingCommandsRef = useRef<Map<string, string>>(new Map());
  const resizeObserversRef = useRef<Map<string, ResizeObserver>>(new Map());
  const outputOffsetsRef = useRef<Map<string, number>>(new Map());
  const [layoutMode, setLayoutMode] = useState<"tabs" | "grid">(() => {
    try {
      return (localStorage.getItem("factory-terminal-layout-mode") as "tabs" | "grid") || "tabs";
    } catch { return "tabs"; }
  });

  const projectTabs = tabs[projectId] || [];
  const activeTab = projectTabs.find((t) => t.id === activeTabId[projectId]);

  const sendResizeIfValid = useCallback(
    (tabId: string, cols: number, rows: number) => {
      if (cols < 2 || rows < 2) return;
      sendResize(projectId, tabId, cols, rows);
    },
    [projectId, sendResize]
  );

  const fitAndSyncTab = useCallback(
    (tabId: string) => {
      const fitAddon = fitAddonsRef.current.get(tabId);
      const terminal = terminalsRef.current.get(tabId);
      if (!fitAddon || !terminal) return;

      try {
        fitAddon.fit();
      } catch {
        // Ignore fit errors.
      }

      sendResizeIfValid(tabId, terminal.cols || 80, terminal.rows || 24);
    },
    [sendResizeIfValid]
  );

  const fitAndSyncTabRef = useRef(fitAndSyncTab);
  useEffect(() => { fitAndSyncTabRef.current = fitAndSyncTab; }, [fitAndSyncTab]);

  // Create initial tab if none exist
  useEffect(() => {
    if (projectTabs.length === 0) {
      createTab(projectId);
    }
  }, [projectId, projectTabs.length, createTab]);

  // Initialize terminal for a tab
  const initializeTerminal = useCallback(
    (tabId: string, container: HTMLDivElement) => {
      // Already initialized?
      if (terminalsRef.current.has(tabId)) {
        return;
      }

      const terminal = new Terminal({
        theme: terminalTheme,
        ...terminalConfig,
      });

      const fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();

      terminal.loadAddon(fitAddon);
      terminal.loadAddon(webLinksAddon);

      terminal.open(container);

      // Capture pointer so selection-drag auto-scroll works when cursor
      // exits the Tauri window at the bottom edge.
      const screenEl = container.querySelector(".xterm-screen") as HTMLElement | null;
      if (screenEl) {
        screenEl.addEventListener("pointerdown", (e: PointerEvent) => {
          screenEl.setPointerCapture(e.pointerId);
        });
      }

      terminalsRef.current.set(tabId, terminal);
      fitAddonsRef.current.set(tabId, fitAddon);
      fitAndSyncTab(tabId);

      // Replay buffered output for this tab (if it already existed before UI remount).
      const existingTab = useTerminalStore.getState().tabs[projectId]?.find((t) => t.id === tabId);
      const full = existingTab?.outputBuffer || "";
      const bufferOffset = existingTab?.bufferOffset || 0;
      if (full) {
        terminal.write(full);
      }
      outputOffsetsRef.current.set(tabId, bufferOffset + full.length);

      // Auto-refit when container resizes (panel drag, window resize, etc.)
      let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
      const observer = new ResizeObserver(() => {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          requestAnimationFrame(() => {
            fitAndSyncTabRef.current(tabId);
          });
        }, 50);
      });
      observer.observe(container);
      resizeObserversRef.current.set(tabId, observer);

      // Handle input
      terminal.onData((data) => {
        sendInput(projectId, tabId, data);
      });

      // Handle resize
      terminal.onResize(({ cols, rows }) => {
        sendResizeIfValid(tabId, cols, rows);
      });

      // Connect to backend — check for pending command (e.g. "claude" or "codex")
      const pendingCmd = pendingCommandsRef.current.get(tabId);
      if (pendingCmd) {
        pendingCommandsRef.current.delete(tabId);
      }
      connectTerminal(tabId, terminal, pendingCmd);
    },
    [projectId, sendInput, fitAndSyncTab, sendResizeIfValid]
  );

  // Connect terminal to WebSocket
  const connectTerminal = useCallback(
    async (tabId: string, terminal: Terminal, command?: string) => {
      const token = await getAuthToken();
      if (!token) {
        updateTabStatus(projectId, tabId, "error", "Authentication required");
        terminal.writeln("\r\n\x1b[31mAuthentication required. Please log in.\x1b[0m\r\n");
        return;
      }

      const state = useTerminalStore.getState();
      const currentTab = state.tabs[projectId]?.find((t) => t.id === tabId);
      if (currentTab?.sessionId && state.connections[currentTab.sessionId]) {
        sendResizeIfValid(tabId, terminal.cols || 80, terminal.rows || 24);
        return;
      }

      terminal.writeln("\x1b[90mConnecting to terminal...\x1b[0m\r\n");

      connect(
        projectId,
        tabId,
        token,
        undefined,
        // onSessionCreated
        (_sessionId) => {
          terminal.writeln("\x1b[32mConnected.\x1b[0m\r\n");
          sendResizeIfValid(tabId, terminal.cols || 80, terminal.rows || 24);
        },
        command
      );
    },
    [projectId, getAuthToken, connect, updateTabStatus, sendResizeIfValid]
  );

  // Launch a tool (Claude Code or Codex) in a new tab
  const launchTool = useCallback(
    (tool: "claude" | "codex" | "gemini") => {
      if (projectTabs.length >= 12) return;
      const label = tool === "claude" ? "Claude Code" : tool === "codex" ? "Codex" : "Gemini";
      const tabId = createTab(projectId, label);
      const command = tool === "codex" ? "codex --no-alt-screen" : tool;

      // We can't call connectTerminal yet because the xterm container
      // hasn't mounted. Store the command so initializeTerminal picks it up.
      pendingCommandsRef.current.set(tabId, command);
    },
    [projectId, projectTabs.length, createTab]
  );

  // Reconnect a terminal
  const handleReconnect = useCallback(
    async (tabId: string) => {
      const terminal = terminalsRef.current.get(tabId);
      if (terminal) {
        await connectTerminal(tabId, terminal);
      }
    },
    [connectTerminal]
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (layoutMode === "tabs") {
        if (activeTab?.id) {
          fitAndSyncTab(activeTab.id);
        }
        return;
      }

      fitAddonsRef.current.forEach((_, tabId) => {
        fitAndSyncTab(tabId);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab?.id, layoutMode, fitAndSyncTab]);

  // Fit active terminal when tab changes
  useEffect(() => {
    if (activeTab) {
      // Small delay to ensure container is visible
      const timer = setTimeout(() => {
        fitAndSyncTab(activeTab.id);
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [activeTab?.id, fitAndSyncTab]);

  // Persist layout mode & refit on mode/tab change
  useEffect(() => {
    try { localStorage.setItem("factory-terminal-layout-mode", layoutMode); } catch {}
    const timer = setTimeout(() => {
      if (layoutMode === "tabs") {
        if (activeTab?.id) {
          fitAndSyncTab(activeTab.id);
        }
        return;
      }
      fitAddonsRef.current.forEach((_, tabId) => {
        fitAndSyncTab(tabId);
      });
    }, 50);
    return () => clearTimeout(timer);
  }, [activeTab?.id, layoutMode, projectTabs.length, fitAndSyncTab]);

  useTerminalFocusRefit(terminalsRef, fitAddonsRef, activeTab?.id, layoutMode, fitAndSyncTab);

  // Stream store buffers into mounted terminals (delta only).
  useEffect(() => {
    projectTabs.forEach((tab) => {
      const terminal = terminalsRef.current.get(tab.id);
      if (!terminal) return;

      const full = tab.outputBuffer || "";
      const bufferOffset = tab.bufferOffset || 0;
      const globalEnd = bufferOffset + full.length;
      const writtenGlobal = outputOffsetsRef.current.get(tab.id) ?? bufferOffset;

      if (writtenGlobal < bufferOffset || writtenGlobal > globalEnd) {
        terminal.reset();
        if (full) {
          terminal.write(full);
        }
        outputOffsetsRef.current.set(tab.id, globalEnd);
        return;
      }

      if (globalEnd > writtenGlobal) {
        const start = writtenGlobal - bufferOffset;
        terminal.write(full.slice(start));
        outputOffsetsRef.current.set(tab.id, globalEnd);
      }
    });

    const ids = new Set(projectTabs.map((t) => t.id));
    Array.from(outputOffsetsRef.current.keys()).forEach((id) => {
      if (!ids.has(id)) outputOffsetsRef.current.delete(id);
    });
  }, [projectTabs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resizeObserversRef.current.forEach((obs) => obs.disconnect());
      resizeObserversRef.current.clear();

      terminalsRef.current.forEach((terminal) => {
        terminal.dispose();
      });
      terminalsRef.current.clear();
      fitAddonsRef.current.clear();
      containerRefs.current.clear();
      containerRefCallbacks.current.clear();
      pendingCommandsRef.current.clear();
      outputOffsetsRef.current.clear();
    };
  }, [projectId]);

  // Handle creating new tab
  const handleNewTab = () => {
    if (projectTabs.length >= 12) {
      // Max tabs reached
      return;
    }
    const tabId = createTab(projectId);
    // Terminal will be initialized when container mounts
  };

  // Handle closing tab
  const handleCloseTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    // Cleanup terminal
    const terminal = terminalsRef.current.get(tabId);
    if (terminal) {
      terminal.dispose();
      terminalsRef.current.delete(tabId);
      fitAddonsRef.current.delete(tabId);
    }

    containerRefs.current.delete(tabId);
    containerRefCallbacks.current.delete(tabId);
    pendingCommandsRef.current.delete(tabId);
    outputOffsetsRef.current.delete(tabId);

    closeTab(projectId, tabId);
  };

  useEffect(() => {
    const ids = new Set(projectTabs.map((tab) => tab.id));
    Array.from(containerRefCallbacks.current.keys()).forEach((id) => {
      if (!ids.has(id)) {
        containerRefCallbacks.current.delete(id);
        containerRefs.current.delete(id);
        pendingCommandsRef.current.delete(id);
        outputOffsetsRef.current.delete(id);
      }
    });
  }, [projectTabs]);

  // Ref callback for terminal container
  const setContainerRef = useCallback(
    (tabId: string) => {
      const existing = containerRefCallbacks.current.get(tabId);
      if (existing) return existing;

      const callback = (el: HTMLDivElement | null) => {
        if (!el) {
          const obs = resizeObserversRef.current.get(tabId);
          if (obs) { obs.disconnect(); resizeObserversRef.current.delete(tabId); }

          containerRefs.current.delete(tabId);
          outputOffsetsRef.current.delete(tabId);

          // If the container is unmounted (layout mode switch, parent remount),
          // dispose terminal so it can be re-mounted cleanly on the next element.
          const terminal = terminalsRef.current.get(tabId);
          if (terminal) {
            terminal.dispose();
            terminalsRef.current.delete(tabId);
          }
          fitAddonsRef.current.delete(tabId);
          return;
        }

        containerRefs.current.set(tabId, el);

        if (!terminalsRef.current.has(tabId)) {
          initializeTerminal(tabId, el);
        }
      };

      containerRefCallbacks.current.set(tabId, callback);
      return callback;
    },
    [initializeTerminal]
  );

  const getStatusDot = (tab: TerminalTab) => {
    switch (tab.status) {
      case "connecting":
        return <Loader2 className="w-3 h-3 animate-spin text-amber-400" />;
      case "connected":
        return <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.5)]" />;
      case "error":
        return <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_4px_rgba(248,113,113,0.5)]" />;
      case "waking":
        return (
          <div className="flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
            <span className="text-xs text-blue-400">Waking...</span>
          </div>
        );
      default:
        return <span className="w-1.5 h-1.5 rounded-full bg-zinc-600" />;
    }
  };

  // Build rows of 2 for grid mode
  const gridRows = (() => {
    const rows: typeof projectTabs[] = [];
    for (let i = 0; i < projectTabs.length; i += 2) {
      rows.push(projectTabs.slice(i, i + 2));
    }
    return rows;
  })();

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Tab bar */}
      <div className="flex items-center h-9 px-1.5 bg-[#111116] border-b border-white/[0.06]">
        <div className="flex items-center gap-0.5 overflow-x-auto min-w-0 flex-1" style={{ scrollbarWidth: "none" }}>
          {projectTabs.map((tab) => (
            <div
              key={tab.id}
              className={cn(
                "group flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-[12px] rounded-md transition-all duration-150",
                "tracking-[-0.01em] select-none font-medium",
                "font-[JetBrains_Mono,Fira_Code,monospace]",
                tab.id === activeTab?.id
                  ? "bg-white/[0.07] text-zinc-200 shadow-[inset_0_0.5px_0_rgba(255,255,255,0.06)]"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03]"
              )}
              data-testid={`terminal-tab-${tab.id}`}
            >
              <button
                type="button"
                onClick={() => setActiveTab(projectId, tab.id)}
                className="flex items-center gap-1.5 min-w-0"
              >
                {getStatusDot(tab)}
                <span className="max-w-[100px] truncate">{tab.name}</span>
              </button>
              {projectTabs.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => handleCloseTab(tab.id, e)}
                  aria-label="Close tab"
                  className="ml-0.5 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/[0.08] text-zinc-500 hover:text-zinc-300 transition-opacity"
                  data-testid={`terminal-close-${tab.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}

          {/* New tab button */}
          {projectTabs.length < 12 && (
            <button
              type="button"
              onClick={handleNewTab}
              aria-label="New terminal tab"
              className="flex items-center justify-center w-6 h-6 rounded text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] transition-colors ml-0.5"
              data-testid="terminal-new-tab"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Separator */}
        <div className="w-px h-4 bg-white/[0.06] mx-1.5" />

        {/* Quick-launch AI tools */}
        {projectTabs.length < 12 && (
          <ToolLauncherButtons onLaunch={launchTool} />
        )}

        {/* Separator */}
        <div className="w-px h-4 bg-white/[0.06] mx-1.5" />

        {/* Layout mode toggle */}
        <button
          type="button"
          onClick={() => setLayoutMode(m => m === "tabs" ? "grid" : "tabs")}
          aria-label={layoutMode === "tabs" ? "Switch to grid layout" : "Switch to tab layout"}
          className={cn(
            "flex items-center justify-center w-6 h-6 rounded transition-colors",
            layoutMode === "grid"
              ? "text-violet-400 bg-violet-500/[0.1]"
              : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]"
          )}
          title={layoutMode === "tabs" ? "Switch to grid layout" : "Switch to tab layout"}
        >
          {layoutMode === "tabs" ? <LayoutGrid className="w-3.5 h-3.5" /> : <Rows2 className="w-3.5 h-3.5" />}
        </button>

        {/* Reconnect button for disconnected/error tabs */}
        {activeTab && (activeTab.status === "disconnected" || activeTab.status === "error") && (
          <button
            type="button"
            onClick={() => handleReconnect(activeTab.id)}
            className="flex items-center gap-1 ml-1.5 px-2 py-1 text-[11px] font-medium text-zinc-400 rounded hover:bg-white/[0.04] hover:text-zinc-200 transition-colors"
            data-testid="terminal-reconnect"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Reconnect</span>
          </button>
        )}
      </div>

      {/* Terminal containers */}

      {/* Container status banner */}
      {isCloudWorkspace && containerStatus === "sleeping" && (
        <div className="flex items-center justify-center gap-2 py-3 bg-zinc-800/50 border-b border-zinc-700/50">
          <Moon className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">Workspace is sleeping</span>
          <button
            onClick={() => activeWorkspace && wakeContainer(activeWorkspace.id)}
            className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-md transition-colors"
          >
            Wake Up
          </button>
        </div>
      )}

      {isCloudWorkspace && containerStatus === "waking" && (
        <div className="flex items-center justify-center gap-2 py-3 bg-zinc-800/50 border-b border-zinc-700/50">
          <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
          <span className="text-sm text-blue-400">Waking workspace... (~30-60 seconds)</span>
        </div>
      )}

      {isCloudWorkspace && containerStatus === "none" && (
        <div className="flex items-center justify-center gap-2 py-3 bg-zinc-800/50 border-b border-zinc-700/50">
          <Cloud className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">No cloud environment set up</span>
          <button
            onClick={() => activeWorkspace && createContainer(activeWorkspace.id)}
            className="px-3 py-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-md transition-colors"
          >
            Create Environment
          </button>
        </div>
      )}

      <div className={cn("flex-1 overflow-hidden relative", "bg-[#0c0c0f]")}>
        {layoutMode === "grid" && projectTabs.length >= 2 ? (
          <PanelGroup direction="vertical" className="h-full">
            {gridRows.map((row, ri) => (
              <Fragment key={`row-${ri}`}>
                {ri > 0 && <PanelResizeHandle className="terminal-handle-v" />}
                <Panel minSize={15}>
                  {row.length === 1 ? (
                    <div
                      ref={setContainerRef(row[0].id)}
                      className={cn(
                        "h-full w-full",
                        row[0].id === activeTab?.id && "ring-1 ring-violet-500/20 rounded-sm"
                      )}
                      onClick={() => setActiveTab(projectId, row[0].id)}
                      data-testid={`terminal-container-${row[0].id}`}
                    />
                  ) : (
                    <PanelGroup direction="horizontal" className="h-full">
                      {row.map((tab, ci) => (
                        <Fragment key={tab.id}>
                          {ci > 0 && <PanelResizeHandle className="terminal-handle-h" />}
                          <Panel minSize={15}>
                            <div
                              ref={setContainerRef(tab.id)}
                              className={cn(
                                "h-full w-full",
                                tab.id === activeTab?.id && "ring-1 ring-violet-500/20 rounded-sm"
                              )}
                              onClick={() => setActiveTab(projectId, tab.id)}
                              data-testid={`terminal-container-${tab.id}`}
                            />
                          </Panel>
                        </Fragment>
                      ))}
                    </PanelGroup>
                  )}
                </Panel>
              </Fragment>
            ))}
          </PanelGroup>
        ) : (
          // Tab mode: all terminals stacked, only active visible
          projectTabs.map((tab) => (
            <div
              key={tab.id}
              ref={setContainerRef(tab.id)}
              className={cn("absolute inset-0 p-2", tab.id === activeTab?.id ? "opacity-100 z-10" : "opacity-0 pointer-events-none z-0")}
              data-testid={`terminal-container-${tab.id}`}
            />
          ))
        )}

        {/* Error overlay */}
        {activeTab?.status === "error" && activeTab.error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-10">
            <div className="bg-[#18181b] border border-red-500/30 rounded-lg p-5 max-w-md text-center shadow-2xl">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
              <p className="text-red-300/90 text-sm leading-relaxed">{activeTab.error}</p>
              <button
                type="button"
                onClick={() => handleReconnect(activeTab.id)}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-zinc-200 bg-white/[0.06] hover:bg-white/[0.1] rounded-md border border-white/[0.08] transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
